import { useSelector } from 'react-redux';
import { useShowToast } from '@/hooks';
import moment from 'moment-timezone';
import { getInvalidVariableError } from '@/utils/commonHelpers';
import { selectUsers } from '@/store/users/selectors';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import {
  useUpdateEmailActivityMessageMutation,
  useCancelEmailActivityMutation,
} from '@/store/query/email-activity';

interface ApplicationData {
  id?: string | number;
  job_id?: string | number;
  [key: string]: any;
}

interface EmailMessage {
  id: string | number;
  subject?: string;
  body?: string;
  attachments?: any[];
  sender?: {
    id?: string | number;
    email?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

interface ScheduleRef {
  current?: {
    submitCount: number;
    values: {
      schedule_at: any;
      [key: string]: any;
    };
    [key: string]: any;
  };
}

interface EmailFormValues {
  messageId: string | number;
  from?: string;
  fromUserObject?: {
    id: string | number;
    [key: string]: any;
  };
  subject?: string;
  body?: string;
  attachments?: any[];
  isScheduled: boolean;
  cc?: string[];
  bcc?: string[];
  [key: string]: any;
}

interface UseEmailActionsOptions {
  onSuccess?: () => void;
  applicationData?: ApplicationData;
}

export const useEmailActions = (options: UseEmailActionsOptions = {}) => {
  const { onSuccess = () => {}, applicationData = {} } = options;

  const users = useSelector(selectUsers);
  const toast = useShowToast();
  const { processFromUserPayload, feSharedEmails } = useGetAllUsers();

  const [updateEmailActivityMessage, { isLoading: isUpdating }] =
    useUpdateEmailActivityMessageMutation();

  const [cancelEmailActivity] = useCancelEmailActivityMutation();

  const handleEditEmailSubmit = async (
    values: EmailFormValues,
    onClose?: () => void,
    scheduleRef?: ScheduleRef
  ): Promise<void> => {
    try {
      let fromPayload: Record<string, any> = {};
      if (feSharedEmails) {
        fromPayload = processFromUserPayload({
          selectedUser: values?.fromUserObject,
          payloadKey: 'sender_id',
        });
      } else {
        const { from } = values;
        const sender_id = users?.find((u) => u?.email === from)?.id;
        fromPayload = { sender_id };
      }

      const { cc, bcc, ...restValues } = values;
      const params: Record<string, any> = {
        ...restValues,
        ...fromPayload,
      };

      // Only include schedule_at if we're scheduling the email
      if (values?.isScheduled && scheduleRef?.current?.values?.schedule_at) {
        params.schedule_at = scheduleRef.current.values.schedule_at;
      } else if (!values?.isScheduled) {
        // If not scheduled, ensure we remove any schedule_at information
        params.schedule_at = null;
      }

      if (values?.selectedTemplate) {
        params.commTempId = values?.selectedTemplate?.id;
      }

      // Add application-specific params if they exist
      if (applicationData?.job_id) {
        params.jobId = applicationData.job_id;
      }

      if (applicationData?.id) {
        params.appIds = [applicationData.id];
      }

      if (Array.isArray(cc) && cc.length > 0) {
        params.cc = cc
          .map((email) => users?.find((user) => user.email === email)?.id)
          .filter(Boolean);
      }
      if (Array.isArray(bcc) && bcc.length > 0) {
        params.bcc = bcc
          .map((email) => users?.find((user) => user.email === email)?.id)
          .filter(Boolean);
      }

      const result = await updateEmailActivityMessage({
        params,
        messageId: values.messageId,
      });

      if ('error' in result) {
        toast({
          title: 'Updating email failed',
          status: 'error',
          description: getInvalidVariableError(result.error),
        });
      } else {
        toast({ title: 'Email updated successfully', status: 'success' });
        onSuccess();
        if (onClose) onClose();
      }
    } catch (error) {
      toast({
        title: 'Failed to update email',
        status: 'error',
        description: 'Something went wrong with updating the email',
      });
    }
  };

  const handleSendNow = async (message: EmailMessage): Promise<void> => {
    // Prevent multiple calls if already updating
    if (isUpdating) {
      console.log('Already updating, preventing multiple calls');
      return;
    }

    try {
      const currentTimezone = moment.tz.guess();
      const currentTime = moment().tz(currentTimezone).format();
      const atsSharedEmail =
        message?.sender?.sent_by_type === 'AtsSharedEmail'
          ? message?.sender?.id
          : null;
      const params = {
        from: null,
        subject: null,
        body: null,
        attachments: null,
        cc: null,
        bcc: null,
        sender_id: null,
        ats_shared_email_id: message?.ats_shared_email_id || atsSharedEmail,
        schedule_at: currentTime,
      };

      const result = await updateEmailActivityMessage({
        params,
        messageId: message.id,
      });

      if ('error' in result) {
        toast({
          title: 'Failed to send email',
          status: 'error',
          description: getInvalidVariableError(result.error),
        });
      } else {
        toast({ title: `Email sending in progress`, status: 'info' });
        onSuccess();
      }
    } catch (error) {
      toast({
        title: 'Failed to send email',
        status: 'error',
        description: 'Something went wrong with sending the email',
      });
    }
  };

  const handleDeleteEmail = async (message: EmailMessage): Promise<void> => {
    try {
      if (!message || !message?.id) {
        toast({
          title: 'Failed to delete email',
          status: 'error',
          description: 'Email data not available',
        });
        return;
      }

      await cancelEmailActivity({
        messageId: message.id,
      });

      toast({
        title: 'Email deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onSuccess();
    } catch (error) {
      toast({
        title: 'Failed to delete email',
        description: error?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return {
    handleEditEmailSubmit,
    handleSendNow,
    handleDeleteEmail,
    isUpdating,
  };
};
